/**
 * Custom Hook for Users Management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  usersService,
  User,
  UsersQuery,
  CreateUserDto,
  UpdateUserDto,
} from '../services/users.service';

interface UseUsersState {
  users: User[];
  selectedUser: User | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

export function useUsers(initialQuery?: UsersQuery) {
  const [state, setState] = useState<UseUsersState>({
    users: [],
    selectedUser: null,
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
    loading: true,
    submitting: false,
    error: null,
  });

  const [query, setQuery] = useState<UsersQuery>(initialQuery || { page: 1, limit: 50 });

  const fetchUsers = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUsers(query);
      if (response.success && response.data) {
        const raw = response.data as any;
        const firstDataLayer = raw?.data;
        const secondDataLayer = firstDataLayer?.data;

        const users: User[] = Array.isArray(raw)
          ? raw
          : Array.isArray(firstDataLayer)
            ? firstDataLayer
            : Array.isArray(secondDataLayer)
              ? secondDataLayer
              : [];

        const meta =
          raw?.meta ??
          firstDataLayer?.meta ??
          secondDataLayer?.meta ??
          {};

        setState((prev) => ({
          ...prev,
          users,
          total: Number(meta.total ?? raw?.total ?? firstDataLayer?.total ?? users.length),
          page: Number(meta.page ?? raw?.page ?? firstDataLayer?.page ?? 1),
          limit: Number(meta.limit ?? raw?.limit ?? firstDataLayer?.limit ?? 50),
          totalPages: Number(meta.totalPages ?? raw?.totalPages ?? firstDataLayer?.totalPages ?? 0),
          loading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load users',
      }));
    }
  }, [query]);

  const fetchUser = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserById(id);
      if (response.success && response.data) {
        setState((prev) => ({ ...prev, selectedUser: response.data!, loading: false }));
      }
    } catch (error: any) {
      setState((prev) => ({ ...prev, loading: false, error: error.message || 'Failed to load user' }));
    }
  }, []);

  const createUser = useCallback(async (data: CreateUserDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const response = await usersService.createUser(data);
      if (response.success) {
        await fetchUsers();
        setState((prev) => ({ ...prev, submitting: false }));
        return response.data;
      }
    } catch (error: any) {
      const message = error.message || 'Failed to create user';
      setState((prev) => ({ ...prev, submitting: false, error: message }));
      throw error;
    }
  }, [fetchUsers]);

  const updateUser = useCallback(async (id: string, data: UpdateUserDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const response = await usersService.updateUser(id, data);
      if (response.success) {
        await fetchUsers();
        setState((prev) => ({ ...prev, submitting: false }));
        return response.data;
      }
    } catch (error: any) {
      const message = error.message || 'Failed to update user';
      setState((prev) => ({ ...prev, submitting: false, error: message }));
      throw error;
    }
  }, [fetchUsers]);

  const deleteUser = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await usersService.deleteUser(id);
      await fetchUsers();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (error: any) {
      const message = error.message || 'Failed to delete user';
      setState((prev) => ({ ...prev, submitting: false, error: message }));
      throw error;
    }
  }, [fetchUsers]);

  const toggleStatus = useCallback(async (id: string) => {
    try {
      await usersService.toggleUserStatus(id);
      await fetchUsers();
    } catch (error: any) {
      console.error('Failed to toggle user status:', error);
      throw error;
    }
  }, [fetchUsers]);

  const updateQuery = useCallback((newQuery: Partial<UsersQuery>) => {
    setQuery((prev) => ({ ...prev, ...newQuery }));
  }, []);

  const refresh = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users: state.users,
    selectedUser: state.selectedUser,
    total: state.total,
    page: state.page,
    limit: state.limit,
    totalPages: state.totalPages,
    loading: state.loading,
    submitting: state.submitting,
    error: state.error,
    query,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    toggleStatus,
    updateQuery,
    refresh,
  };
}